import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';

// own functions
import * as ownFunctions from './ContractBranchSelector.Functions';
import { Field, Label, Tag, TagPicker, TagPickerControl, TagPickerGroup, TagPickerInput, TagPickerList, TagPickerOption, TagPickerProps, Spinner, Button } from '@fluentui/react-components';
import { ArrowClockwise24Regular } from '@fluentui/react-icons';


const ContractBranchSelectorComponent = (props: ownFunctions.Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    if(props.Contract === undefined)
      props.GetContractById(props.ContractId);
  }, [props.ContractId]);

  useEffect(() => {
    if(props.Contract && (props.Contract.addendums === undefined || props.Contract.addendums.length === 0) && props.AddendumId !== undefined && props.AddendumId !== null)
      props.GetContractAddendumsByContractId(props.Contract.id);
    /* Values, not the contract object: it is replaced on every import status poll. */
  }, [props.Contract?.id, props.Contract?.addendums?.length, props.AddendumId]);

  useEffect(() => {
      if (props.ContractId) {
        props.GetAllContractsBranchs(props.ContractId);
      }
  }, [props.ContractId]);
  
  const [internalBranchesSelected, setInternalBranchesSelected] = useState<ContractBranch[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ContractBranch[]>([]);

  useEffect(() => {
    if (props.ContractBranchesSelected && props.ContractBranchesSelected.length > 0) {
      const selectedBranches: ContractBranch[] = [];
      props.ContractBranchesSelected.forEach(selected => {
        if (typeof selected === 'number' || typeof selected === 'string') {
          const branch = props.ContractBranches.find(b => b.id.toString() === selected.toString());
          if (branch) {
            selectedBranches.push(branch);
          }
        } else if (typeof selected === 'object') {
          selectedBranches.push(selected as ContractBranch);
        }
      }
      );
      setInternalBranchesSelected(selectedBranches);
      setSelectedOptions(selectedBranches);
    } else if (props.ContractBranchesSelected?.length === 0) {
      setInternalBranchesSelected([]);
      setSelectedOptions([]);
    }
  }, [props.ContractBranchesSelected, props.ContractBranches]);

  const tagPickerOptions = props.MultiselectEnable
    ? props.ContractBranches?.filter((branch) => !internalBranchesSelected.some(selected => selected.id === branch.id))
    : props.ContractBranches;

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (event, data) => {
      const newSelectedOptions: ContractBranch[] = [];
      
      data.selectedOptions.forEach(selectedName => {
        const branch = props.ContractBranches.find(b => b.branchName === selectedName);
        if (branch) {
          newSelectedOptions.push(branch);
        }
      });
      
      setInternalBranchesSelected(newSelectedOptions);
      setSelectedOptions(newSelectedOptions);
      props.onSelectedContractBranch(newSelectedOptions);
  }

  const handleRefresh = () => {
    if (props.ContractId) {
      props.GetAllContractsBranchs(props.ContractId);
    }
  };

  return (<>
      <Field label={t('ContractBranchSelector.label', 'Select a branch')} style={{ maxWidth: 400 }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={internalBranchesSelected.map(option => option.branchName)}
          disabled={props.isLoading || (!props.isLoading && props.ContractBranches.length === 0)}
        >
          <TagPickerControl
            expandIcon={props.isLoading ? <Spinner size="tiny" /> : undefined}
          >
            <TagPickerGroup aria-label={t('ContractBranchSelector.selectedAria', 'Selected branches')}>
              {selectedOptions.map((option) => (
                <Tag
                  key={option.id}
                  shape="rounded"
                  value={option.branchName}
                >
                  {option.branchName}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput 
              aria-label={t('ContractBranchSelector.inputAria', 'Select a branch')} 
              placeholder={
                props.isLoading 
                  ? t('ContractBranchSelector.loading', 'Loading...') 
                  : (!props.isLoading && props.ContractBranches.length === 0)
                    ? t('ContractBranchSelector.noBranches', 'No branches available')
                    : undefined
              }
            />
          </TagPickerControl>
          <TagPickerList>
            {tagPickerOptions?.length > 0 ? (
              tagPickerOptions?.map((option) => (
                <TagPickerOption
                  value={option.branchName}
                  key={option.id}
                  text={option.branchName}
                >
                  {option.branchName}
                </TagPickerOption>
              ))
            ) : (<></>)}
          </TagPickerList>
        </TagPicker>
          </div>
          <Button 
            icon={<ArrowClockwise24Regular />} 
            onClick={handleRefresh}
            disabled={props.isLoading}
            title={t('ContractBranchSelector.refresh', 'Refresh branches')}
            appearance="subtle"
          />
        </div>
        {props.noteText && <Label>{props.noteText}</Label>}
      </Field>
  </>);
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ContractBranchSelectorComponent as any);
